import { createAsyncThunk } from "@reduxjs/toolkit";
import DATAMART_API from "dot/Constants/services/datamart";
import { IFavoritesFilterObject, IOfferDeleteObject, IOfferDuplicateObject, IOfferFilterObject, IOfferPropertiesObject, IOfferProps } from "../types";
import DATAMART_SERVICES from "dot/Constants/services/datamart_services";
import { setError } from "Store/general/auth";
import { setProperty } from "../store/dashboardSlice";
import CONFIG_API from "dot/Constants/services/configurator";

// Define an async thunk for fetching user data
export const fetchOffer = createAsyncThunk<
  IOfferProps[] | {}, // Return type (e.g., array of offers)
  IOfferFilterObject, // Argument type (currentPage)
  { rejectValue: string } // For rejected actions
>(
  "dashboard/fetchOffer", // Action type
  async (offerFilterObj, thunkAPI) => {
    const {
      caseType,
      offerType,
      currentPage,
      itemsPerPage,
      sortOption,
      offerSearch,
      offerStatus,
    } = offerFilterObj;
    try {
      const dasboardParam = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        caseType,
        offerType,
        offerStatus,
        sort: sortOption,
        search: offerSearch,
      };
      // Make service call to fetch offer data and offer status count
      const { offerData, statusCount } = await DATAMART_SERVICES.fetchOfferDataAndStatusCount(dasboardParam);
      return { offerData, statusCount }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message); // Rejected action
    }
  }
);


// Define an async thunk for updating properties
export const updateProperties = createAsyncThunk<
  IOfferProps[], // Return type (e.g., array of offers)
  IOfferPropertiesObject, // Argument type (currentPage)
  { rejectValue: string } // For rejected actions
>(
  "dashboard/editProperties", // Action type
  async (editPropertiesObj, { dispatch, rejectWithValue }) => {
    try {
      const resp = await DATAMART_SERVICES.updateOfferProperties(editPropertiesObj);
      if (resp?.status === true) {
        // Dispatch additional actions here if needed
        dispatch(
          setError({
            key: "info",
            value: {
              errorCode: null,
              errorMsg: "Properties updated successfully!",
            },
          })
        );
      }
      else {
        const errorCode = resp?.errorCode ? resp?.errorCode : "API_ERROR";
        const errorMessage = resp?.errorMessage ? resp?.errorMessage : "Failed to update properties. Please try again.";
        // Handle errors with a separate dispatch
        dispatch(
          setError({
            key: "error",
            value: {
              errorCode: errorCode,
              errorMsg: errorMessage,
            },
          })
        );
      }

      return resp; // Fulfilled action
    } catch (error) {
      // Handle errors with a separate dispatch
      dispatch(
        setError({
          key: "error",
          value: {
            errorCode: "API_ERROR",
            errorMsg: "Failed to update properties. Please try again.",
          },
        })
      );
      return rejectWithValue(error.message); // Rejected action
    }
  }
);

/**
 * Handles the deletion of an offer by calling the DATAMART_API.
 *
 * @returns {Promise<any>} - A promise that resolves to the delete status.
 * @throws Will dispatch an error action if the deletion process fails.
 */

export const handleDeleteOffer = createAsyncThunk<
  // IOfferProps[], // Return type (e.g., array of offers)
  any,
  IOfferDeleteObject, // Argument type (currentPage)
  { rejectValue: string } // For rejected actions
>(
  "dashboard/handleDeleteOffer", // Action type
  async (deleteData, { dispatch, rejectWithValue }) => {
    try {
      let resp = await DATAMART_API.handleDeleteOffer(deleteData);
      if (resp && resp[0]?.status === true) {
        // Dispatch additional actions here if needed
        dispatch(
          setError({
            key: "info",
            value: {
              errorCode: null,
              errorMsg: "Offer deleted successfully!",
            },
          })
        );
        return resp; // Fulfilled action
      }
      else {
        // api response error
        return handleErrorResponse(resp, dispatch, rejectWithValue);
      }
    } catch (error) {
      // network errors
      return handleErrorResponse(error, dispatch, rejectWithValue);
    }
  }
);


// Define an async thunk for fetching user favorites
export const fetchUserFavourites = createAsyncThunk<
  IOfferProps[] | {}, // Return type (e.g., array of offers)
  IFavoritesFilterObject, // Argument type (currentPage)
  { rejectValue: string } // For rejected actions
>(
  "dashboard/fetchUserFavourites", // Action type
  async (offerFilterObj, thunkAPI) => {
    const {
      caseType,
      currentPage,
      itemsPerPage,
      sortOption,
      offerSearch,
    } = offerFilterObj;
    try {
      const dasboardParam = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        caseType,
        sort: sortOption,
        search: offerSearch,
      };
      // Make service call to fetch offer data and offer status count
      const favoritesData = await DATAMART_SERVICES.fetchUserFavoritesOffer(dasboardParam);
      return favoritesData; // Fulfilled action
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message); // Rejected action
    }
  }
);

/**
 * Asynchronously retrieves offer details using the provided offer identifiers.
 *
 * This thunk dispatches an API call to fetch details of an offer. If the API call fails,
 * an error message is dispatched via the setError action, and the promise is rejected with the error message.
 *
 * @async
 * @param {Object} params - The parameters for fetching offer details.
 * @param {string} params.offer_id - The identifier of the offer.
 * @param {string} params.temp_offer_id - A temporary identifier associated with the offer.
 * @param {Object} thunkAPI - The thunk API object containing additional properties.
 * @param {Function} thunkAPI.dispatch - Function used to dispatch actions.
 * @param {Function} thunkAPI.rejectWithValue - Function to reject the thunk with a value.
 * @returns {Promise<any>} The offer details response if the API call is successful.
 * @throws {string} An error message if the API call fails.
 */
export const getOfferDetails = createAsyncThunk<
  any,
  { offer_id: string; temp_offer_id: string },
  { rejectValue: string }
>(
  "dashboard/getOfferDetails",
  async ({ offer_id, temp_offer_id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await DATAMART_SERVICES.getOfferDetails({ offer_id, temp_offer_id });
      return response;
    } catch (error) {
      dispatch(
        setError({
          key: "error",
          value: {
            errorCode: "API_ERROR",
            errorMsg: "Failed to get offer details. Please try again.",
          },
        })
      );
      return rejectWithValue(error.message);
    }
  }
);

export const handleDuplicateOffer = createAsyncThunk<
  any,
  IOfferDuplicateObject,
  { rejectValue: string }
>(
  "dashboard/handleDuplicateOffer",
  async (duplicateData, { dispatch, rejectWithValue }) => {
    try {
      let resp = await DATAMART_SERVICES.handleDuplicateOffer(duplicateData);
      if (resp && resp?.status === true) {
        // Dispatch success notification
        dispatch(
          setError({
            key: "info",
            value: {
              errorCode: null,
              errorMsg: "Offer duplicated successfully!",
            },
          })
        );
        dispatch(
          setProperty({ key: "clonedOffer", value: true })
        );
      } else {
        return handleErrorResponse(resp, dispatch, rejectWithValue);
      }
    } catch (error) {
      return handleErrorResponse(error, dispatch, rejectWithValue);
    }
  }
);

export const cloneOfferAtConfigurator = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "dashboard/cloneOfferAtConfigurator",
  async (parentOfferId, { dispatch, rejectWithValue }) => {
    try {
      if (!parentOfferId || parentOfferId.length === 0) {
        dispatch(
          setError({
            key: "error",
            value: {
              errorCode: "ERROR",
              errorMsg: "Unable to proceed with cloning due to a system requirement issue.",
            },
          })
        );
        return;
      }
      // Make an api call to get parentSid from DM
      // Make use of available method - getSavedUiState
      const offerResposne = await DATAMART_SERVICES.getSavedUiState(parentOfferId);           
      let usiJsonOBj;
      try {
        // Parse the offer response to extract the SID
        // Assuming the response is a JSON string, parse it to an object
        // If the response is already an object, this will not throw an error
        usiJsonOBj = JSON.parse(offerResposne);
      } catch (parseError) {
        dispatch(
          setError({
            key: "error",
            value: {
              errorCode: "ERROR",
              errorMsg: "Failed to parse saved offer response data.",
            },
          })
        );
        return rejectWithValue("Failed to parse saved offer response data.");
      }

      // Check if usiJsonOBj is valid and has the required structure
      if (!usiJsonOBj?.dynamic?.data?.sid || !(usiJsonOBj?.dynamic?.data?.sid && usiJsonOBj?.dynamic?.data?.sid?.length > 0)) {
        dispatch(
          setError({
            key: "error",
            value: {
              errorCode: "ERROR",
              errorMsg: "Unable to proceed with cloning due to a system requirement issue.",
            },
          })
        );
        return rejectWithValue("Unable to proceed with cloning due to a system requirement issue.");
      }

      // Get the parent sid from the response
      const parentSid = usiJsonOBj?.dynamic?.data?.sid;
      const response = await CONFIG_API.getSidFromConfigurator(parentSid);

      if (response && response.status) {
        return response;
      } else {
        return handleErrorResponse(response, dispatch, rejectWithValue);
      }
    } catch (error) {
      return handleErrorResponse(error, dispatch, rejectWithValue);
    }
  }
);

function handleErrorResponse(resp, dispatch, rejectWithValue) {
  const errorCode = resp?.errorCode ? resp?.errorCode : "API_ERROR";
  const errorMessage = resp?.errorMessage ? resp?.errorMessage : "Failed to do the operation. Please try again.";
  // Handle errors with a separate dispatch
  dispatch(
    setError({
      key: "error",
      value: {
        errorCode: errorCode,
        errorMsg: errorMessage,
      },
    })
  );
  // Reject the promise with the error, making it available in the rejected state
  return rejectWithValue(errorMessage || "Failed to do the operation. Please try again.");
}

